import { useState, useCallback, useEffect, useMemo } from "react";
import { trpc } from "@/hooks/trpc";
import { useRoomWebSocket } from "@/hooks/use-room-websocket";
import { TimelineBar } from "@/features/room-display/CompactSchedulePanel";
import { CompactBookingFlow } from "@/features/room-display/CompactBookingFlow";
import { UpcomingCard } from "@/features/room-display/UpcomingCard";
import { Wifi, WifiOff } from "lucide-react";
import { formatTime } from "@/lib/utils";
import type { Booking } from "@scheduler/shared";

export function RoomDisplay({ roomId }: { roomId: string }) {
  const [bookingSlot, setBookingSlot] = useState<{
    start: Date;
    end: Date;
  } | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  const today = currentTime.toISOString().split("T")[0];

  const { data: room, isLoading: roomLoading } =
    trpc.rooms.getById.useQuery({ id: roomId });
  const { data: bookings, isLoading: bookingsLoading } =
    trpc.bookings.listByRoom.useQuery(
      { room_id: roomId, date: today },
      { refetchInterval: 30_000, staleTime: 0, gcTime: 0 },
    );

  const utils = trpc.useUtils();

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 10_000);
    return () => clearInterval(interval);
  }, []);

  const handleWsUpdate = useCallback(
    (message: { type: string; bookings?: Booking[]; booking?: Booking }) => {
      if (
        message.type === "schedule_updated" ||
        message.type === "booking_created" ||
        message.type === "booking_cancelled"
      ) {
        utils.bookings.listByRoom.invalidate({ room_id: roomId, date: today });
      }
    },
    [utils, roomId, today],
  );

  const { connected: wsConnected } = useRoomWebSocket(roomId, handleWsUpdate);

  const status = useMemo(() => {
    if (!bookings)
      return {
        state: "available" as const,
        currentBooking: null,
        nextBooking: null,
        freeUntil: null,
      };

    const now = currentTime.getTime();
    const sorted = [...bookings].sort(
      (a, b) =>
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
    );

    let currentBooking: Booking | null = null;
    let nextBooking: Booking | null = null;

    for (const booking of sorted) {
      const start = new Date(booking.start_time).getTime();
      const end = new Date(booking.end_time).getTime();
      if (start <= now && end > now) {
        currentBooking = booking;
      } else if (start > now && !nextBooking) {
        nextBooking = booking;
      }
    }

    if (currentBooking) {
      return { state: "busy" as const, currentBooking, nextBooking, freeUntil: null };
    }

    return {
      state: "available" as const,
      currentBooking: null,
      nextBooking,
      freeUntil: nextBooking ? new Date(nextBooking.start_time) : null,
    };
  }, [bookings, currentTime]);

  const handleReserve = () => {
    const now = new Date();
    now.setSeconds(0, 0);
    const end = status.freeUntil ?? (() => { const d = new Date(now); d.setHours(23, 59, 0, 0); return d; })();
    if (now >= end) return;
    setBookingSlot({ start: now, end });
  };

  const timeStr = currentTime.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (roomLoading || bookingsLoading) {
    return (
      <div className="h-screen w-screen bg-neutral-900 flex items-center justify-center">
        <div className="h-6 w-6 rounded-full border-2 border-white/20 border-t-white/60 animate-spin" />
      </div>
    );
  }

  if (!room) {
    return (
      <div className="h-screen w-screen bg-neutral-900 flex items-center justify-center">
        <p className="text-lg text-white/50">Room not found</p>
      </div>
    );
  }

  const isAvailable = status.state === "available";

  let timeRemaining = "";
  let progressPct = 0;
  if (status.currentBooking) {
    const startMs = new Date(status.currentBooking.start_time).getTime();
    const endMs = new Date(status.currentBooking.end_time).getTime();
    const diffMin = Math.max(0, Math.round((endMs - currentTime.getTime()) / 60_000));
    if (diffMin >= 60) {
      const h = Math.floor(diffMin / 60);
      const m = diffMin % 60;
      timeRemaining = m > 0 ? `${h}h ${m}m remaining` : `${h}h remaining`;
    } else {
      timeRemaining = `${diffMin}m remaining`;
    }
    const total = endMs - startMs;
    const elapsed = currentTime.getTime() - startMs;
    progressPct = Math.min(100, Math.max(0, (elapsed / total) * 100));
  }

  return (
    <>
      <div
        className="h-screen w-screen flex flex-col overflow-hidden transition-colors duration-700"
        style={{
          background: isAvailable
            ? "linear-gradient(135deg, #1a3a1a 0%, #0d1f0d 50%, #0a160a 100%)"
            : "linear-gradient(135deg, #3a2a1a 0%, #1f150d 50%, #16100a 100%)",
        }}
      >
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-white/90 tracking-tight">
              {room.name}
            </h1>
            {room.location && (
              <span className="text-sm text-white/30">{room.location}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              {wsConnected ? (
                <Wifi className="h-3 w-3 text-white/30" />
              ) : (
                <WifiOff className="h-3 w-3 text-white/20" />
              )}
            </div>
            <span className="text-sm text-white/40 tabular-nums">{timeStr}</span>
          </div>
        </div>

        <UpcomingCard bookings={bookings ?? []} currentTime={currentTime} />

        <div className="flex-1 flex flex-col items-center justify-center px-6">
          {isAvailable ? (
            <>
              <p className="text-5xl font-bold tracking-tight text-farmless-green-7">
                Available
              </p>
              {status.freeUntil ? (
                <p className="mt-2 text-lg text-white/40">
                  until {formatTime(status.freeUntil.toISOString())}
                </p>
              ) : (
                <p className="mt-2 text-lg text-white/30">All day</p>
              )}
              <button
                onClick={handleReserve}
                className="mt-6 rounded-full px-8 py-3 text-base font-semibold text-farmless-green-9 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: "linear-gradient(135deg, #9bcf7e 0%, #81b465 100%)",
                  boxShadow: "0 4px 24px rgba(155, 207, 126, 0.25)",
                }}
              >
                Book This Room
              </button>
            </>
          ) : (
            <>
              <p className="text-5xl font-bold tracking-tight text-amber-400">
                Occupied
              </p>
              <p className="mt-3 text-xl font-medium text-white/70 max-w-md truncate">
                {status.currentBooking!.title}
              </p>
              <p className="mt-1 text-sm text-white/40">
                {status.currentBooking!.organizer_name}
              </p>
              <div className="mt-5 flex items-center gap-3 w-64">
                <div className="flex-1 h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-amber-400 transition-all duration-1000"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <span className="text-xs text-white/40 shrink-0 tabular-nums">
                  {timeRemaining}
                </span>
              </div>
            </>
          )}
        </div>

        <div className="px-6 pb-3">
          <TimelineBar bookings={bookings ?? []} currentTime={currentTime} />
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-1.5 opacity-30">
              <img src="/farmless-icon.svg" alt="" className="h-3" />
              <span className="text-[10px] text-white font-medium">Farmless</span>
            </div>
            <span className="text-[10px] text-white/20">
              {currentTime.toLocaleDateString([], {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>
      </div>

      {bookingSlot && (
        <CompactBookingFlow
          roomId={roomId}
          slotStart={bookingSlot.start}
          slotEnd={bookingSlot.end}
          onClose={() => setBookingSlot(null)}
        />
      )}
    </>
  );
}
